/**
 * Modality detection — is this app instance running inside a Telegram
 * Mini-App webview, or as a stand-alone PWA in a regular browser?
 *
 * Treat "running as a Mini-App" as "Telegram injected window.Telegram.WebApp
 * AND that WebApp has a non-empty initData query string." The empty-initData
 * case happens in dev when the @twa-dev/sdk shim is loaded but no real
 * Telegram host is present, so we explicitly reject it.
 */

interface TelegramWebAppGlobal {
  initData: string;
  ready?: () => void;
  expand?: () => void;
  /**
   * Bot API 8.0+ (iOS / Android Telegram 11.0+). Asks the host to put the
   * Mini-App into device-level fullscreen, hiding Telegram's chrome and
   * the status bar. Older clients leave this undefined — feature-detect
   * before calling and ignore the failure event.
   */
  requestFullscreen?: () => void;
  isFullscreen?: boolean;
  /**
   * Bot API 6.9+ (iOS / Android Telegram 9.7+). Shows a native popup
   * asking the user to share their Telegram-verified phone number with
   * the bot. Callback fires with `shared=true|false` once the user
   * dismisses the popup; the contact itself is delivered to the bot via
   * a normal message{contact} webhook update — NOT to the webview — so
   * the frontend has to poll or refetch the user to detect persistence.
   */
  requestContact?: (callback: (shared: boolean) => void) => void;
}

interface TelegramGlobal {
  WebApp?: TelegramWebAppGlobal;
}

declare global {
  interface Window {
    Telegram?: TelegramGlobal;
  }
}

export const isTelegramWebApp = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }
  const initData = window.Telegram?.WebApp?.initData;
  return typeof initData === 'string' && initData.length > 0;
};

export const tg = (): TelegramWebAppGlobal | undefined =>
  typeof window === 'undefined' ? undefined : window.Telegram?.WebApp;
